/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { colors } from '../../../shared/theme/colors';
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  businessName: string;
  onChangeBusinessName: (value: string) => void;
  /** Nombre del dueño (customer-facing + comunicaciones). Requerido. */
  ownerName?: string;
  onChangeOwnerName?: (value: string) => void;
  referralCode?: string;
  onChangeReferralCode?: (value: string) => void;
  showReferralCodeField?: boolean;
  labelInputRef?: React.RefObject<TextInput | null>;
  disabled?: boolean;
  canProceed?: boolean;
  onSubmit?: () => void;
};

/**
 * Paso "identity" del wizard de onboarding: un solo campo para el nombre del
 * local. Extraído de OrganizationSetupWizard para desacoplar el render del
 * paso del state machine y la chrome del wizard.
 */
export function IdentityStepPanel({
  businessName,
  onChangeBusinessName,
  ownerName,
  onChangeOwnerName,
  referralCode,
  onChangeReferralCode,
  showReferralCodeField = false,
  labelInputRef,
  disabled = false,
  canProceed = false,
  onSubmit,
}: Props) {
  const ownerInputRef = useRef<TextInput>(null);
  const [focusedField, setFocusedField] = useState<'business' | 'owner' | null>(null);

  const handleNameSubmit = () => {
    if (canProceed && !disabled && onSubmit) {
      onSubmit();
    } else if (onChangeOwnerName) {
      ownerInputRef.current?.focus();
    }
  };

  return (
    <View style={styles.body}>
      <View style={styles.field}>
        <Text style={styles.label}>Nombre del local</Text>
        <TextInput
          ref={labelInputRef}
          value={businessName}
          onChangeText={onChangeBusinessName}
          placeholder="Ej. Kiosco Don Pepe"
          placeholderTextColor={ticketColors.ink3}
          style={[
            styles.input,
            businessName.trim().length > 0 && styles.inputValid,
            focusedField === 'business' && styles.inputFocused,
          ]}
          editable={!disabled}
          autoFocus
          returnKeyType={canProceed ? 'go' : 'next'}
          onSubmitEditing={handleNameSubmit}
          onFocus={() => setFocusedField('business')}
          onBlur={() => setFocusedField(null)}
          accessibilityLabel="Nombre del local"
          testID="onboarding.business-name"
        />
      </View>
      {onChangeOwnerName ? (
        <View style={styles.field}>
          <Text style={styles.label}>¿Cómo te llamás?</Text>
          <TextInput
            ref={ownerInputRef}
            value={ownerName ?? ''}
            onChangeText={onChangeOwnerName}
            placeholder="Ej. Juan"
            placeholderTextColor={ticketColors.ink3}
            style={[
              styles.input,
              (ownerName ?? '').trim().length > 0 && styles.inputValid,
              focusedField === 'owner' && styles.inputFocused,
            ]}
            editable={!disabled}
            returnKeyType="done"
            onSubmitEditing={() => {
              if (canProceed && !disabled && onSubmit) onSubmit();
            }}
            onFocus={() => setFocusedField('owner')}
            onBlur={() => setFocusedField(null)}
            accessibilityLabel="Tu nombre"
            testID="onboarding.owner-name"
          />
        </View>
      ) : null}
      {showReferralCodeField && onChangeReferralCode ? (
        <View style={styles.field}>
          <Text style={styles.label}>Código de quien te invitó (opcional)</Text>
          <Text style={styles.hint}>Si entraste por un link, ya lo cargamos.</Text>
          <TextInput
            value={referralCode ?? ''}
            onChangeText={(t) => onChangeReferralCode(t.toUpperCase())}
            placeholder="Ej. MARIA80"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="characters"
            autoCorrect={false}
            style={styles.input}
            editable={!disabled}
            accessibilityLabel="Código de quien te invitó"
            testID="onboarding.referral-code"
          />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  body: { gap: spacing.md },
  field: { gap: spacing.xs },
  label: {
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0,
    textTransform: 'none',
    color: ticketColors.ink2,
  },
  hint: {
    fontSize: 12,
    color: ticketColors.ink3,
    lineHeight: 16,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: colors.controlBorder ?? ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    color: ticketColors.ink,
    fontSize: 16,
  },
  /** Mismo ring brand que el search del picker (paso 1). */
  inputFocused: {
    borderColor: ticketColors.brandPrimary,
    boxShadow: '0 0 0 3px rgba(15, 118, 110, 0.14)',
  } as any,
  inputValid: { borderColor: `${ticketColors.success}66` },
});
